import { useTranslation } from "react-i18next";
import UserTaskListItemDto from "../../types/dtos/tasks/advanced-tasks/UserTaskListItemDto";
import { ModalPopup } from "../common";
import taskRestrictionHelper from "../../helpers/taskRestrictionHelper";
import { CountRestrictionType } from "../../types/dtos/tasks/advanced-tasks/CountRestrictionType";
import { ClientTaskTypeCategory } from "../../types/dtos/tasks/advanced-tasks/ClientTaskTypeCategory";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClientTaskType } from "../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import UserTaskListGroupDto from "../../types/dtos/tasks/advanced-tasks/UserTaskListGroupDto";
import { useState } from "react";
import parse from "html-react-parser";
import cx from "classnames";
import AdvancedTaskDto from "../../types/dtos/forms/AdvancedTaskDto";
import AdvancedToDoListItemDto from "../../types/dtos/dashboards/right-bar/to-do-list/AdvancedToDoListItemDto";

type OriginLocation = "JOURNEY" | "OUTSIDE-JOURNEY";

function getBadgeLabelClassName({
  requirementsMet,
  origin,
  fullLengthMode,
  subtleMode,
}: {
  requirementsMet: boolean;
  origin: OriginLocation;
  fullLengthMode: boolean;
  subtleMode: boolean;
}): string {
  const textColor = requirementsMet ? "text-green-600" : "text-rose-600";
  const isOutsideJourney = origin === "OUTSIDE-JOURNEY";

  const backgroundClass = requirementsMet
    ? isOutsideJourney
      ? "bg-green-200/75 rounded-full"
      : "bg-green-200 rounded-full"
    : isOutsideJourney
      ? "bg-rose-200/75 rounded-full"
      : "bg-rose-200 rounded-full";

  const borderClass = requirementsMet ? "border-green-500" : "border-rose-500";

  return cx(
    "badge-label leading-tight select-none text-xs",
    textColor,
    origin === "JOURNEY" && "mr-2",
    !subtleMode && ["px-2 py-0.5 rounded-sm", backgroundClass],
    fullLengthMode && [
      "!text-sm block",
      !subtleMode && "!rounded border",
      !subtleMode && borderClass,
    ],
  );
}

interface TaskRequirementBadgeProps {
  tasks: UserTaskListItemDto[] | AdvancedToDoListItemDto[] | AdvancedTaskDto[];
  minTasks: number | null;
  maxTasks: number | null;
  countRestriction: CountRestrictionType;
  activeCategories: ClientTaskTypeCategory[] | null;
  taskType: ClientTaskType;
  isNonEmployeeViewer: boolean;
  userGroup?: UserTaskListGroupDto;
  showInfoIcon?: boolean;
  origin: OriginLocation;
  journeySubjectFirstName?: string | null;
  fullLengthMode?: boolean;
  subtleMode?: boolean;
  hideWhenValid?: boolean;
  containerClassNames?: string;
}

const TaskRequirementBadge = ({
  tasks,
  minTasks,
  maxTasks,
  countRestriction,
  activeCategories,
  taskType,
  isNonEmployeeViewer,
  userGroup,
  showInfoIcon = true,
  origin,
  journeySubjectFirstName,
  fullLengthMode = false,
  subtleMode = false,
  hideWhenValid = false,
  containerClassNames,
}: TaskRequirementBadgeProps) => {
  const { t } = useTranslation();
  const [modalIsOpen, setModalIsOpen] = useState(false);

  let personToken = isNonEmployeeViewer ? userGroup?.employeeFirstName! : null;

  if (origin == "JOURNEY" && journeySubjectFirstName) {
    personToken = journeySubjectFirstName;
  }

  if (countRestriction === "NONE") {
    return null;
  }

  const taskRestrictionResult =
    taskRestrictionHelper.doesUserHaveTasksThatSatisfyRestrictions(
      tasks,
      minTasks,
      maxTasks,
      countRestriction,
      activeCategories,
    );
  const doesUserHaveTaskRestrictions = taskRestrictionResult.satisfied;

  if (hideWhenValid && doesUserHaveTaskRestrictions) {
    return null;
  }

  const taskRestrictionText =
    taskRestrictionHelper.getRestrictionBannerTextHtmlForManagementPages(
      taskType,
      activeCategories,
      countRestriction,
      minTasks,
      maxTasks,
      personToken,
      t,
    );

  const infoPopup = (
    <ModalPopup
      isOpen={modalIsOpen}
      onOpenChange={setModalIsOpen}
      onPrimaryButtonClick={() => setModalIsOpen(false)}
      primaryButtonText={t("Common.Close")}
      title={t("Pages.SendNow.Tooltips.Information")}
      showCloseIcon={false}
      width="SMALL"
    >
      <div className="styled-list">
        {parse(taskRestrictionText ? taskRestrictionText : "")}
      </div>
    </ModalPopup>
  );

  const openModal = () => {
    if (!taskRestrictionText) return;
    // Only open the modal if there is restriction text to show
    setModalIsOpen(true);
  };

  const badgeContent = (
    <>
      <button
        onClick={openModal}
        className={cx(
          taskRestrictionText ? "hover:cursor-pointer" : "hover:cursor-default",
          fullLengthMode ? "w-full" : "",
        )}
      >
        <span
          className={getBadgeLabelClassName({
            requirementsMet: doesUserHaveTaskRestrictions,
            origin,
            fullLengthMode,
            subtleMode,
          })}
        >
          {showInfoIcon && (
            <FontAwesomeIcon icon={faInfoCircle} size="sm" className="mr-1" />
          )}
          {doesUserHaveTaskRestrictions
            ? t("TaskType.Restrictions.TaskRequirementsMet").replace(
                "#TASK_TYPE#",
                t(taskType.singularNameTranslationKeyIdentifier),
              )
            : t("TaskType.Restrictions.TaskRequirementsNotMet").replace(
                "#TASK_TYPE#",
                t(taskType.singularNameTranslationKeyIdentifier),
              )}
        </span>
      </button>
      {infoPopup}
    </>
  );

  if (containerClassNames) {
    return <div className={containerClassNames}>{badgeContent}</div>;
  }

  return badgeContent;
};

export default TaskRequirementBadge;
